import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { createSlug, stringify } from '../../util/urlHelpers';
import { NamedLink } from '../../components';

import css from './TransactionPanel.module.css';

export const HEADING_ENQUIRED = 'enquired';
export const HEADING_ENQUIREDRFQ = 'enquired-rfq';
export const HEADING_REQUESTED = 'requested';
export const HEADING_OFFER_RECEIVED = 'offer-received';
export const HEADING_COUNTEROFFER_RECEIVED = 'counteroffer-received';
export const HEADING_COUNTEROFFER_RECEIVED_FROM_PROVIDER = 'counteroffer-received-from-provider';
export const HEADING_CANCELED = 'canceled';
export const HEADING_CANCELED_BY_PROVIDER = 'canceled-by-provider';
export const HEADING_OFFER_APPROVED = 'offer-approved';
export const HEADING_OFFER_APPROVED_BY_PROVIDER = 'offer-approved-by-provider';
export const HEADING_UPLOAD_DOCUMENTS = 'upload-documents';
export const HEADING_IN_PROGRESS = 'in-progress';
export const HEADING_DECLINED = 'declined';
export const HEADING_COMPLETE_OFFER = 'request-complete-offer';
export const HEADING_DELIVERED = 'delivered';
export const HEADING_CANCELED_BY_OPERATOR = 'canceled-by-operator';
export const HEADING_DELIVERED_BY_OPERATOR = 'delivered-by-operator';

export const HEADING_PREAUTHORIZED = 'preauthorized';
export const HEADING_EXPIRED = 'expired';
export const HEADING_PAYMENT_EXPIRED = 'payment-expired';
export const HEADING_ACCEPTED = 'accepted';
export const HEADING_REJECTED = 'rejected';
export const HEADING_FINALIZED = 'finalized';

const createListingLink = (listingId, label, listingDeleted, searchParams = {}, className = '') => {
  if (!listingDeleted) {
    const params = { id: listingId, slug: createSlug(label) };
    const to = { search: stringify(searchParams) };
    return (
      <NamedLink className={className} name="ListingPage" params={params} to={to}>
        {label}
      </NamedLink>
    );
  } else {
    return <FormattedMessage id="TransactionPanel.deletedListingOrderTitle" />;
  }
};

const ListingDeletedInfoMaybe = props => {
  return props.listingDeleted ? (
    <p className={css.transactionInfoMessage}>
      <FormattedMessage id="TransactionPanel.messageDeletedListing" />
    </p>
  ) : null;
};

const HeadingCustomer = props => {
  const { className, id, values, listingDeleted } = props;
  return (
    <React.Fragment>
      <h1 className={className}>
        <span className={css.mainTitle}>
          <FormattedMessage id={id} values={values} />
        </span>
      </h1>
      <ListingDeletedInfoMaybe listingDeleted={listingDeleted} />
    </React.Fragment>
  );
};

const HeadingCustomerWithSubtitle = props => {
  const { className, id, values, subtitleId, subtitleValues, children, listingDeleted } = props;
  return (
    <React.Fragment>
      <h1 className={className}>
        <span className={css.mainTitle}>
          <FormattedMessage id={id} values={values} />
        </span>
        <FormattedMessage id={subtitleId} values={subtitleValues} />
      </h1>
      {children}
      <ListingDeletedInfoMaybe listingDeleted={listingDeleted} />
    </React.Fragment>
  );
};

const CustomerBannedInfoMaybe = props => {
  return props.isCustomerBanned ? (
    <p className={css.transactionInfoMessage}>
      <FormattedMessage id="TransactionPanel.customerBannedStatus" />
    </p>
  ) : null;
};

const HeadingProvider = props => {
  const { className, id, values, isCustomerBanned, children } = props;
  return (
    <React.Fragment>
      <h1 className={className}>
        <span className={css.mainTitle}>
          <FormattedMessage id={id} values={values} />
        </span>
      </h1>
      {children}
      <CustomerBannedInfoMaybe isCustomerBanned={isCustomerBanned} />
    </React.Fragment>
  );
};

// Functional component as a helper to choose and show Order or Sale heading info:
// title, subtitle, and message
const PanelHeading = props => {
  const {
    className,
    rootClassName,
    panelHeadingState,
    customerName,
    providerName,
    listingId,
    listingTitle,
    listingDeleted,
    isCustomerBanned,
  } = props;

  const isCustomer = props.transactionRole === 'customer';

  const defaultRootClassName = isCustomer ? css.headingOrder : css.headingSale;
  const titleClasses = classNames(rootClassName || defaultRootClassName, className);
  const listingLink = createListingLink(listingId, listingTitle, listingDeleted);

  switch (panelHeadingState) {
    case HEADING_ENQUIRED:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderEnquiredTitle"
          values={{ listingLink }}
          listingDeleted={listingDeleted}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleEnquiredTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_ENQUIREDRFQ:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderEnquiredRFQTitle"
          values={{ listingLink }}
          listingDeleted={listingDeleted}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleEnquiredRFQTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_OFFER_RECEIVED:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderSentQuotationTitle"
          values={{ customerName, listingLink }}
          listingDeleted={listingDeleted}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleSentQuotationTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_COUNTEROFFER_RECEIVED:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderSentCounterQuotationTitle"
          values={{ customerName, listingLink }}
          listingDeleted={listingDeleted}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleSentCounterQuotationTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_COUNTEROFFER_RECEIVED_FROM_PROVIDER:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.saleSentCounterFromProviderQuotationTitle"
          values={{ providerName, listingLink }}
          listingDeleted={listingDeleted}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.orderSentCounterQuotationTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_CANCELED:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderCancelledTitle"
          values={{ providerName, listingLink }}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleCancelledTitle"
          values={{ providerName, listingLink }}
        />
      );
    case HEADING_CANCELED_BY_PROVIDER:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.saleCancelledTitle"
          values={{ providerName, listingLink }}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.orderCancelledTitle"
          values={{ providerName, listingLink }}
        />
      );
    case HEADING_OFFER_APPROVED:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderAcceptedTitle"
          values={{ customerName, listingLink }}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleAcceptedTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_OFFER_APPROVED_BY_PROVIDER:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.saleCouterAcceptedTitle"
          values={{ providerName, listingLink }}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.counterOrderAcceptedTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_UPLOAD_DOCUMENTS:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderContractStarted"
          values={{ providerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleContractStarted"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_IN_PROGRESS:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderContractStarted"
          values={{ providerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleContractStarted"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_COMPLETE_OFFER:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderRequestedInfo"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleRequestedInfo"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_DECLINED:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderDeclinedCloseTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleDeclinedCloseTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_DELIVERED:
      return isCustomer ? (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.orderDeliveredTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      ) : (
        <HeadingProvider
          className={titleClasses}
          id="TransactionPanel.saleDeliveredTitle"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      );
    case HEADING_CANCELED_BY_OPERATOR:
      return (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.canceledByOpertor"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      )
    case HEADING_DELIVERED_BY_OPERATOR:
      return (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.deliveredByOpertor"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      )

    case HEADING_PREAUTHORIZED:
      return (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.headingPreauthorized"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      )
    case HEADING_EXPIRED:
      return (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.headingExpired"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      )
    case HEADING_PAYMENT_EXPIRED:
      return (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.headingPaymentExpired"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      )
    case HEADING_ACCEPTED:
      return (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.headingAccepted"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      )
    case HEADING_REJECTED:
      return (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.headingRejected"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      )
    case HEADING_FINALIZED:
      return (
        <HeadingCustomer
          className={titleClasses}
          id="TransactionPanel.headingFinalized"
          values={{ customerName, listingLink }}
          isCustomerBanned={isCustomerBanned}
        />
      )
    default:
      console.warn('Unknown state given to panel heading.');
      return null;
  }
};

export default PanelHeading;
